/**
 * Feature Flags Constants
 *
 * Single source of truth for all available feature flags.
 * Shared between frontend and backend - TypeScript enforces valid keys.
 *
 * ## Adding a New Feature Flag
 *
 * 1. Add to FeatureFlag constant below
 * 2. Add metadata to FEATURE_FLAG_METADATA in backend
 * 3. TypeScript enforces it everywhere
 * 4. Seed DB (happens automatically from metadata)
 *
 * @example
 * ```typescript
 * // Frontend usage
 * const isEnabled = useFeatureFlag(FeatureFlag.SURFER_AGENT);
 *
 * // Backend usage
 * @RequireFeature(FeatureFlag.SURFER_AGENT)
 * ```
 */

/**
 * Feature Flag Keys
 *
 * Type-safe enum-like constant for all feature flags.
 * Using SCREAMING_SNAKE_CASE for keys, snake_case for values (DB keys).
 */
export const FeatureFlag = {
  /** Surfer AI Agent - Browser automation */
  SURFER_AGENT: "surfer_agent",

  /** Enterprise Audit Logs - Echo system */
  ECHO_AUDIT_LOGS: "echo_audit_logs",

  /** Advanced Workflows - Complex logic features */
  ADVANCED_WORKFLOWS: "advanced_workflows",

  /** Multiple Twilio Connections - Allow orgs to create multiple Twilio subaccounts */
  MULTIPLE_TWILIO_CONNECTIONS: "multiple_twilio_connections",

  /** Office Add-ins - Outlook, Word, PowerPoint, Excel add-in deployments */
  DEPLOY_OFFICE_ADDINS: "deploy_office_addins",

  /** SMS deployment type */
  DEPLOY_SMS: "deploy_sms",

  /** Facebook Messenger deployment type */
  DEPLOY_FB_MESSENGER: "deploy_fb_messenger",

  /** Airtable deployment type */
  DEPLOY_AIRTABLE: "deploy_airtable",

  /** Email Notifications - Organization notification preferences and email dispatch */
  EMAIL_NOTIFICATIONS: "email_notifications",

  /** Cue - AI assistant side panel */
  CUE: "cue",

  /** Inbox uses Conversations module - routes /app/inbox to the new pane */
  INBOX_USE_CONVERSATIONS: "inbox_use_conversations",

  /** Workspaces - org-shared file workspaces (nav entry + agent-edit tab) */
  WORKSPACES: "workspaces",

  /** Vibe - Internal deployment platform (git + build + deploy) for enterprise tenants */
  VIBE: "vibe",

  /**
   * Vibe node auto-remediation — per tenant cluster, may the control plane mark
   * a Nomad client node that has been down past the threshold Unhealthy, so its
   * Auto Scaling group replaces it. An automated production write, so it is
   * whitelist-only: off, the node is still observed and reported, never acted on.
   */
  VIBE_NODE_AUTO_REMEDIATION: "vibe_node_auto_remediation",

  /**
   * Vibe git-host auto-reboot — per tenant cluster, may the control plane REBOOT
   * the git-host node once Nomad has reported it down past the threshold. A
   * separate flag from node replacement because it is a separate act: the
   * git-host is never replaced (its volume is the tenant's source history), and
   * a reboot keeps that volume attached.
   */
  VIBE_GITHOST_AUTO_REBOOT: "vibe_githost_auto_reboot",

  /**
   * Vibe reserve-half-limit — per tenant cluster, whether its tasks reserve in
   * the halved resource shape (half the memory, a quarter of the CPU weight,
   * the limit kept at the quota) instead of reserving their whole budget.
   * Flipping it moves every serving app in the tenant into the new reservation
   * once — make-before-break, a few at a time — so it is whitelist-only.
   */
  VIBE_RESERVE_HALF_LIMIT: "vibe_reserve_half_limit",

  /** Prompt Eval - golden conversations + per-message evaluation (system B's replacement) */
  PROMPT_EVAL: "prompt_eval",

  /** Direct Meta Cloud API path (Messenger + WhatsApp) — the Twilio→Meta cutover master switch */
  META_NATIVE_DIRECT: "meta_native_direct",

  /**
   * Chat Retention - per-deployment policy that archives, deletes or shreds
   * conversations inactive for N days. Off: the setting is hidden and the
   * retention sweep skips the org even when a policy is stored.
   */
  CHAT_RETENTION: "chat_retention",

  /**
   * ElevenLabs voice — lets a WhatsApp deployment speak its voice replies with
   * ElevenLabs instead of OpenAI, and audition ElevenLabs voices. Off: the
   * provider is hidden, a save choosing it is refused, its preview is refused,
   * and a deployment that already stores it speaks with OpenAI.
   *
   * It cannot be turned ON until the backend has `ELEVENLABS_API_KEY`: every
   * enabling door refuses without it, and the admin screen says so.
   */
  ELEVENLABS_VOICE: "elevenlabs_voice",

  /**
   * ROLELESS (legacy) — the org opts OUT of the Role primitive entirely and
   * keeps working the way it did before Roles existed.
   *
   * 🚨 **THIS IS THE ONLY SWITCH FOR THE ROLE PRIMITIVE, AND IT READS
   * BACKWARDS FROM EVERY OTHER FLAG IN THIS CATALOG.** Everywhere else the
   * question is "is this feature on for the org". Here it is "has the org opted
   * out", so every consumer states the equivalence explicitly:
   *
   *     roles are enabled for an org  ≡  NOT roleless_legacy
   *
   * Backend routes spell it `@RequireFeatureDisabled(FeatureFlag.ROLELESS_LEGACY)`;
   * runtime checks read this key and branch on `enabled` meaning OPTED OUT;
   * the frontend nav spells it `requireFlagDisabled`. Three spellings, one
   * polarity, and each one names "disabled" so a reader cannot mistake it for
   * the ordinary direction.
   *
   * Named negatively on purpose, and this is the one place the reasoning is
   * written down. Roles are the platform's default: most orgs are on them,
   * every org created from here on starts on them, and only a named minority
   * stays behind. A positively-named ROLES_ENABLED would default OFF, so every
   * one of those future orgs would land in legacy mode and need a manual flip
   * that someone eventually forgets — a silent regression with no failing
   * signal. Defaulting this OFF puts the majority and the future on the correct
   * side with no row at all, and costs a row only for each exception.
   *
   * 🔴 **Do not add a second permanent roles flag.** It is the attractive move
   * and it has been made three times — one for the access gate, one for the
   * permission-set model, one named `roles` for the primitive itself — each
   * argued from a real distinction, so an org could run Roles as containment
   * first and start enforcing later. Every one of them shipped with an override
   * on no organization; the per-org staging never happened, and the extra
   * switches were wired in parallel with each other: several ways to say the
   * same thing and several ways to disagree with it. Stage a SLICE with a flag
   * that names the slice, and DELETE that flag the moment the slice ships —
   * never with a second permanent switch for the primitive.
   *
   * `roleless_legacy` is the ONLY roles flag. `roles_job_model` was the last
   * slice gate and it is gone: its four tabs (Scope, Job types, Assumptions,
   * Policies) are bound to their write path and released, so the gate had
   * nothing left to hold back, and its own `defaultEnabled: false` would have
   * turned those tabs OFF in any environment whose row it had not yet seeded.
   * A released slice keeping its gate is a switch waiting to be flipped the
   * wrong way by an environment nobody remembers to visit.
   * `feature-flag-suppression.spec.ts` asserts the family stays at one member.
   */
  ROLELESS_LEGACY: "roleless_legacy"
} as const;

/**
 * Type-safe feature flag key
 *
 * Auto-derived from FeatureFlag constant.
 * TypeScript enforces only valid keys can be used.
 *
 * @example
 * ```typescript
 * const key: FeatureFlagKey = 'surfer_agent'; // OK
 * const bad: FeatureFlagKey = 'surfer_agen'; // TypeScript error!
 * ```
 */
export type FeatureFlagKey = (typeof FeatureFlag)[keyof typeof FeatureFlag];

/**
 * Get all defined feature flag keys as array
 *
 * Useful for iteration, validation, etc.
 *
 * @returns Array of all valid feature flag keys
 */
export function getAllFeatureFlagKeys(): FeatureFlagKey[] {
  return Object.values(FeatureFlag);
}

/**
 * Check if a string is a valid feature flag key
 *
 * @param key - String to validate
 * @returns True if key exists in FeatureFlag
 */
export function isValidFeatureFlagKey(key: string): key is FeatureFlagKey {
  return getAllFeatureFlagKeys().includes(key as FeatureFlagKey);
}
